import { motion } from 'framer-motion';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import type { Conversion, ConversionStatus } from '@types';
import { formatDistanceToNow } from 'date-fns';

interface RecentActivityProps {
  conversions: Conversion[];
  loading?: boolean;
  onDownload?: (conversionId: string) => void;
  onRetry?: (conversionId: string) => void;
}

const statusConfig: Record<
  ConversionStatus,
  {
    label: string;
    color: string;
    bgColor: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  }
> = {
  PENDING: {
    label: 'Pending',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    icon: ClockIcon,
  },
  PROCESSING: {
    label: 'Processing',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    icon: ArrowPathIcon,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    icon: CheckCircleIcon,
  },
  FAILED: {
    label: 'Failed',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: XCircleIcon,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    icon: XCircleIcon,
  },
};

export default function RecentActivity({
  conversions,
  loading = false,
  onDownload,
  onRetry,
}: RecentActivityProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (conversions.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="bg-white overflow-hidden shadow rounded-lg"
      >
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="text-center py-12">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No conversions yet
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by converting your first file.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => navigate('/converter')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Start Converting
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className="bg-white overflow-hidden shadow rounded-lg"
    >
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Recent Activity
          </h3>
          <button
            onClick={() => navigate('/history')}
            className="text-sm font-medium text-primary-600 hover:text-primary-500"
          >
            View all
          </button>
        </div>
        <div className="overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {conversions.map((conversion, index) => {
              const status = statusConfig[conversion.status];
              const StatusIcon = status.icon;

              return (
                <motion.li
                  key={conversion.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="py-4 hover:bg-gray-50 transition-colors duration-150"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className={`${status.bgColor} rounded-full p-2`}>
                        <StatusIcon
                          className={`h-5 w-5 ${status.color}`}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {conversion.file_name}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {conversion.source_system}
                        </span>
                        <span className="text-gray-300">•</span>
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(
                            new Date(conversion.created_at),
                            {
                              addSuffix: true,
                            }
                          )}
                        </span>
                        {conversion.record_count !== undefined && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span className="text-xs text-gray-500">
                              {conversion.record_count} records
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}
                      >
                        {status.label}
                      </span>
                    </div>
                    <div className="flex-shrink-0 flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/history/${conversion.id}`)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors duration-150"
                        title="View details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      {conversion.status === 'COMPLETED' && onDownload && (
                        <button
                          onClick={() => onDownload(conversion.id)}
                          className="p-2 text-gray-400 hover:text-primary-600 rounded-full hover:bg-primary-50 transition-colors duration-150"
                          title="Download"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                      )}
                      {conversion.status === 'FAILED' && onRetry && (
                        <button
                          onClick={() => onRetry(conversion.id)}
                          className="p-2 text-gray-400 hover:text-primary-600 rounded-full hover:bg-primary-50 transition-colors duration-150"
                          title="Retry"
                        >
                          <ArrowPathIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}
